import React, { useCallback, useState } from "react"
import {
  Backdrop,
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material"
import PropTypes from "prop-types"
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material"
import { useDropzone } from "react-dropzone"
import axios from "axios"
import useImageManager from "../../../manager/image"
import UseApiManager from "../../../manager/api"
import ImageUploadListView from "./list"
import _ from "ansuko"
import { RefreshTree } from "../../../manager/carto"

const MAX_RESIZE_WIDTH = 1200

const THUMB_MAX_HEIGHT = 500

const getTreeIdFromFilepath = (filepath) => {
  // <tree_id>/<適当なファイル名>.pngのリスト
  const match = filepath.match(/^\/(Tre[0-9]{8})\//)
  if (!match) { return null }
  return match[1]
}

const dataUrlToBlob = (dataUrl) => {
  return fetch(dataUrl).then((r) => r.blob())
}

const getS3PutHeadersFromSignedUrl = (signedUrl) => {
  const headers = {
    "Content-Type": "image/png",
  }
  let url
  try {
    url = new URL(signedUrl)
  } catch (e) {
    return headers
  }

  const acl = url.searchParams.get("x-amz-acl")
  const contentType = url.searchParams.get("Content-Type")
  if (acl) {
    headers["x-amz-acl"] = acl
  }
  if (contentType) {
    headers["Content-Type"] = contentType
  }
  return headers
}

const ImageUploadView = ({ open, onClose }) => {
  const [acceptedFiles, setAcceptedFiles] = useState([])
  const [uploading, setUploading] = useState(false)

  const [uploadResult, setUploadResult] = useState(null)

  const { resizeFromFile } = useImageManager()
  const { Put, GetFirst } = UseApiManager()

  const handleClose = useCallback(() => {
    setAcceptedFiles([])
    setUploadResult(null)
    onClose?.()
  }, [onClose])

  const onDrop = useCallback((files) => {
    setAcceptedFiles((prev) => [...prev, ...files])
    setUploadResult(null)
  }, [])

  const handleUpload = useCallback(async () => {
    if (!acceptedFiles?.length) return
    setUploading(true)
    setUploadResult(null)
    const errors = []
    let successCount = 0
    const uploads = {}

    // アップロードデータ収集
    for (const file of acceptedFiles) {
      const treeId = getTreeIdFromFilepath(file.path)
      const thumb_data = await resizeFromFile(file, 256, null, true, true)
      const org_data = await resizeFromFile(file, 1024, null, false, true)
      const { path, signed_url } = await GetFirst("photo/signed")
      uploads[treeId] = [
        ..._.castArray(uploads[treeId]),
        {
          thumb_url: signed_url.thumb,
          org_url: signed_url.org,
          thumb_data,
          org_data,
          path,
          file,
        }
      ]
    }

    // すべての処理を実行
    await Promise.allSettled(Object.entries(uploads).map(async([tree_id, dataList]) => {
      try {
        await Promise.all(dataList.flatMap(({
          org_url,
          thumb_url,
          org_data,
          thumb_data,
        }) => [
            axios.put(org_url, org_data, { headers: getS3PutHeadersFromSignedUrl(org_url) }),
            axios.put(thumb_url, thumb_data, { headers: getS3PutHeadersFromSignedUrl(thumb_url) })
        ]))
      } catch {
        errors.push({
          message: 'File upload error',
        })
        return
      }

      const paths = dataList.map(d => d.path)
      try {
        await Put("photo/tree", {tree_id, paths})
      } catch {
        errors.push({
          message: "Photo data update error",
        })
      }

      successCount += 1
      setUploadResult({
        success: successCount,
        failed: errors.length,
        errors,
      })      
    }))

    await RefreshTree()

    setAcceptedFiles([])
    setUploading(false)
    // await Promise.allSettled(uploads.forEach(async ({
    //   file,
    //   org_url,
    //   thumb_url,
    //   org_data,
    //   thumb_data,
    //   path
    // }) => {      
    //   await Promise.all([
    //     axios.put(org_url, org_data, { headers: getS3PutHeadersFromSignedUrl(org_url) }),
    //     axios.put(thumb_url, thumb_data, {headers: getS3PutHeadersFromSignedUrl(thumb_url)})
    //   ]).catch(e => {
    //     errors.push({
    //       file,
    //       message: 'File upload error',
    //     })
    //   })

    //   setUploadResult({
    //     success: successCount,
    //     failed: errors.length,
    //     errors,
    //   })
    // }))

    // for (let i = 0; i < acceptedFiles.length; i++) {
    //   const file = acceptedFiles[i]
    //   const tree_id = getTreeIdFromFilepath(file.path)
    //   if (!tree_id) {
    //     errors.push({
    //       file: file.name,
    //       message: "TreeID ? not found",
    //     })
    //     continue
    //   }
    //   try {
    //     const orgData = await resizeFromFile(file, 1200, null, false, true)
    //     const thumbData = await resizeFromFile(file, 256,  null, true, true)
        
    //     const { path, signed_url } = await GetFirst("photo/signed")
    //     uploadProcesses.push(axios.put(signed_url.org, orgData, { headers: getS3PutHeadersFromSignedUrl(signed_url.org) }))
    //     uploadProcesses.push(axios.put(signed_url.thumb, orgData, { headers: getS3PutHeadersFromSignedUrl(signed_url.thumb) }))
    //     updateProcesses[tree_id] = [
    //       ...(uploadProcesses[tree_id] ?? []),
    //       path
    //     ]
    //   } catch (e) {
        // const status = e?.response?.status
        // const body = e?.response?.data
        // let message = e?.response?.data?.message ?? e?.message ?? String(e)
        // if (status) {
        //   message = `[${status}] ${message}`
        // }
        // if (body && typeof body === "object" && !body.message) {
        //   const detail = JSON.stringify(body)
        //   if (detail !== "{}") message = `${message} — ${detail}`
        // }
        // errors.push({
        //   file: file.name,
        //   message,
        // })
//      }
    // }
    // await Promise.all(uploadProcesses)
    // await Promise.all(Object.entries(updateProcesses).map(([tree_id, paths]) => 
    //   Put("photo/tree", {tree_id, paths})
    // ))

    // setUploadResult({
    //   success: successCount,
    //   failed: errors.length,
    //   errors,
    // })
    // setUploading(false)
  }, [acceptedFiles, resizeFromFile, Put, GetFirst])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    noClick: true,
    accept: {
      "image/png": [".png"],
    },
    onDrop,
  })

  return (
    <Backdrop
      open={open}
      onClick={handleClose}
      sx={{
        zIndex: 8000,
        bgcolor: "rgba(0,0,0,0.5)",
      }}
    >
      <Paper
        elevation={8}
        onClick={(e) => e.stopPropagation()}
        sx={{
          width: 720,
          minHeight: 420,
          maxWidth: "90vw",
          maxHeight: "85vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            bgcolor: "grey.200",
            borderBottom: "1px solid",
            borderColor: "grey.300",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CloudUploadIcon sx={{ color: "grey.700", fontSize: 28 }} />
            <Typography
              variant="h6"
              sx={{ color: "grey.800", fontWeight: 600, fontSize: "1.1rem" }}
            >
              樹木画像アップロード
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label="閉じる"
            sx={{ color: "grey.700" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          {...getRootProps()}
          sx={{
            flex: 1,
            minHeight: 360,
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
            bgcolor: isDragActive ? "primary.light" : "grey.50",
            border: "2px dashed",
            borderColor: isDragActive ? "primary.main" : "grey.300",
            transition: "background-color 0.2s, border-color 0.2s",
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: isDragActive ? "primary.light" : "grey.100",
            },
          }}
        >
          <input {...getInputProps()} />
          {isDragActive ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                color: "primary.dark",
              }}
            >
              <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                ここに画像をドロップ
              </Typography>
            </Box>
          ) : (
            <ImageUploadListView acceptedFiles={acceptedFiles} />
          )}
        </Box>
        {acceptedFiles?.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1,
              px: 2,
              py: 1.5,
              borderTop: "1px solid",
              borderColor: "grey.300",
              bgcolor: "grey.50",
            }}
          >
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={uploading}
              startIcon={
                uploading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <CloudUploadIcon />
                )
              }
            >
              {uploading ? "アップロード中..." : "アップロード実行"}
            </Button>
          </Box>
        )}
      </Paper>
    </Backdrop>
  )
}

ImageUploadView.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
}

export default ImageUploadView
